import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../context/NotificationContext';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import './Invoicing.css';

const Invoicing = () => {
  const { t } = useTranslation();
  const { showError } = useNotification();
  const [weeks, setWeeks] = useState(1);
  const [loading, setLoading] = useState(false);
  const [invoicingData, setInvoicingData] = useState(null);
  const [activeTab, setActiveTab] = useState('restaurants'); // 'restaurants', 'stores', 'drivers'

  useEffect(() => {
    fetchInvoicingData();
  }, [weeks]);

  const fetchInvoicingData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/invoicing?weeks=${weeks}`);
      setInvoicingData(response.data);
    } catch (error) {
      console.error('Error fetching invoicing data:', error);
      showError(t('admin.errorLoadingInvoicing'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `€${(amount || 0).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="invoicing-container">
        <div className="loading-spinner">{t('admin.loading')}</div>
      </div>
    );
  }

  return (
    <div className="invoicing-container">
      <div className="invoicing-header">
        <h2>{t('admin.invoicing')}</h2>
        <div className="period-selector">
          <label>{t('admin.selectPeriod')}:</label>
          <select 
            value={weeks} 
            onChange={(e) => setWeeks(parseInt(e.target.value))}
            className="weeks-select"
          >
            {[1, 2, 3, 4, 5, 6, 7, 8].map(w => (
              <option key={w} value={w}>
                {w} {w === 1 ? t('admin.week') : t('admin.weeks')}
              </option>
            ))}
          </select>
        </div>
      </div>

      {invoicingData && (
        <>
          <div className="period-info">
            <p>
              {t('admin.period')}: {formatDate(invoicingData.period.startDate)} - {formatDate(invoicingData.period.endDate)}
            </p>
          </div>

          <div className="tabs">
            <button
              className={`tab ${activeTab === 'restaurants' ? 'active' : ''}`}
              onClick={() => setActiveTab('restaurants')}
            >
              {t('admin.restaurants')} ({invoicingData.restaurantInvoices.length})
            </button>
            <button
              className={`tab ${activeTab === 'stores' ? 'active' : ''}`}
              onClick={() => setActiveTab('stores')}
            >
              {t('admin.stores')} ({invoicingData.storeInvoices.length})
            </button>
            <button
              className={`tab ${activeTab === 'drivers' ? 'active' : ''}`}
              onClick={() => setActiveTab('drivers')}
            >
              {t('admin.deliveryDrivers')} ({invoicingData.driverInvoices.length})
            </button>
          </div>

          {activeTab === 'drivers' && (
            <div className="driver-payout-summary">
              <strong>{t('admin.totalDriverPayout')}:</strong>{' '}
              <span className="payout-amount">{formatCurrency(invoicingData.totalDriverPayout ?? 0)}</span>
              <span className="payout-note">{t('admin.totalDriverPayoutNote')}</span>
            </div>
          )}

          <div className="invoices-content">
            {activeTab === 'restaurants' && (
              <div className="invoices-list">
                {invoicingData.restaurantInvoices.length === 0 ? (
                  <div className="no-invoices">{t('admin.noRestaurantInvoices')}</div>
                ) : (
                  invoicingData.restaurantInvoices.map(invoice => (
                    <div key={invoice.id} className="invoice-card">
                      <div className="invoice-header">
                        <h3>{invoice.name}</h3>
                        <div className="invoice-total">
                          {t('admin.totalEarnings')}: <strong>{formatCurrency(invoice.totalEarnings)}</strong>
                        </div>
                      </div>
                      <div className="invoice-details">
                        <div className="detail-row">
                          <span>{t('admin.totalOrders')}:</span>
                          <span>{invoice.totalOrders}</span>
                        </div>
                        <div className="detail-row">
                          <span>{t('admin.totalSubtotal')}:</span>
                          <span>{formatCurrency(invoice.totalSubtotal)}</span>
                        </div>
                        <div className="detail-row">
                          <span>{t('admin.totalServiceFees')}:</span>
                          <span>-{formatCurrency(invoice.totalServiceFees)}</span>
                        </div>
                        <div className="detail-row">
                          <span>{t('admin.totalAppFees')}:</span>
                          <span>-{formatCurrency(invoice.totalAppFees)}</span>
                        </div>
                        <div className="detail-row total">
                          <span>{t('admin.totalEarnings')}:</span>
                          <span><strong>{formatCurrency(invoice.totalEarnings)}</strong></span>
                        </div>
                      </div>
                      <details className="orders-details">
                        <summary>{t('admin.viewOrders')} ({invoice.orders.length})</summary>
                        <div className="orders-list">
                          {invoice.orders.map((order, idx) => (
                            <div key={idx} className="order-item">
                              <div className="order-info">
                                <span className="order-number">{order.orderNumber}</span>
                                <span className="order-date">{formatDate(order.date)}</span>
                              </div>
                              <div className="order-amounts">
                                <span>{formatCurrency(order.subtotal)}</span>
                                <span>-{formatCurrency(order.serviceFee)}</span>
                                <span>-{formatCurrency(order.appFee)}</span>
                                <span className="order-earnings">{formatCurrency(order.earnings)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'stores' && (
              <div className="invoices-list">
                {invoicingData.storeInvoices.length === 0 ? (
                  <div className="no-invoices">{t('admin.noStoreInvoices')}</div>
                ) : (
                  invoicingData.storeInvoices.map(invoice => (
                    <div key={invoice.id} className="invoice-card">
                      <div className="invoice-header">
                        <h3>{invoice.name}</h3>
                        <div className="invoice-total">
                          {t('admin.totalEarnings')}: <strong>{formatCurrency(invoice.totalEarnings)}</strong>
                        </div>
                      </div>
                      <div className="invoice-details">
                        <div className="detail-row">
                          <span>{t('admin.totalOrders')}:</span>
                          <span>{invoice.totalOrders}</span>
                        </div>
                        <div className="detail-row">
                          <span>{t('admin.totalSubtotal')}:</span>
                          <span>{formatCurrency(invoice.totalSubtotal)}</span>
                        </div>
                        <div className="detail-row">
                          <span>{t('admin.totalServiceFees')}:</span>
                          <span>-{formatCurrency(invoice.totalServiceFees)}</span>
                        </div>
                        <div className="detail-row">
                          <span>{t('admin.totalAppFees')}:</span>
                          <span>-{formatCurrency(invoice.totalAppFees)}</span>
                        </div>
                        <div className="detail-row total">
                          <span>{t('admin.totalEarnings')}:</span>
                          <span><strong>{formatCurrency(invoice.totalEarnings)}</strong></span>
                        </div>
                      </div>
                      <details className="orders-details">
                        <summary>{t('admin.viewOrders')} ({invoice.orders.length})</summary>
                        <div className="orders-list">
                          {invoice.orders.map((order, idx) => (
                            <div key={idx} className="order-item">
                              <div className="order-info">
                                <span className="order-number">{order.orderNumber}</span>
                                <span className="order-date">{formatDate(order.date)}</span>
                              </div>
                              <div className="order-amounts">
                                <span>{formatCurrency(order.subtotal)}</span>
                                <span>-{formatCurrency(order.serviceFee)}</span>
                                <span>-{formatCurrency(order.appFee)}</span>
                                <span className="order-earnings">{formatCurrency(order.earnings)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'drivers' && (
              <div className="invoices-list">
                {invoicingData.driverInvoices.length === 0 ? (
                  <div className="no-invoices">{t('admin.noDriverInvoices')}</div>
                ) : (
                  invoicingData.driverInvoices.map(invoice => (
                    <div key={invoice.id} className="invoice-card driver-invoice-card">
                      <div className="invoice-header">
                        <h3>{invoice.name}</h3>
                        <div className="invoice-total">
                          {t('admin.amountToPayDriver')}: <strong>{formatCurrency(invoice.totalEarnings)}</strong>
                        </div>
                      </div>
                      <div className="invoice-details">
                        <div className="detail-row">
                          <span>{t('admin.email')}:</span>
                          <span>{invoice.email}</span>
                        </div>
                        <div className="detail-row">
                          <span>{t('admin.totalDeliveries')}:</span>
                          <span>{invoice.totalDeliveries}</span>
                        </div>
                        <div className="detail-row total">
                          <span>{t('admin.amountToPayDriver')}:</span>
                          <span><strong>{formatCurrency(invoice.totalEarnings)}</strong></span>
                        </div>
                      </div>
                      <details className="orders-details">
                        <summary>{t('admin.viewDeliveries')} ({invoice.deliveries.length})</summary>
                        <div className="orders-list">
                          {invoice.deliveries.map((delivery, idx) => (
                            <div key={idx} className="order-item">
                              <div className="order-info">
                                <span className="order-number">{delivery.orderNumber}</span>
                                <span className="order-date">{formatDate(delivery.date)}</span>
                                {delivery.distance > 0 && (
                                  <span className="order-distance">{delivery.distance.toFixed(2)} km</span>
                                )}
                              </div>
                              <div className="order-amounts">
                                <span className="order-earnings" title={t('admin.payoutForDelivery')}>
                                  {formatCurrency(delivery.earnings)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Invoicing;
